import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Heart, Send, Share2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MemeStudio() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [memes, setMemes] = useState([]);
  const [newMemeCaption, setNewMemeCaption] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('drake-preference');
  const [aiContext, setAiContext] = useState('');
  const [generatingCaption, setGeneratingCaption] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  useEffect(() => {
    fetchMemes();
  }, []);

  const fetchMemes = async () => {
    try {
      const response = await axios.get(`${API}/memes?limit=50`);
      setMemes(response.data);
    } catch (error) {
      toast.error('Failed to fetch memes');
      console.error('Error:', error);
    }
  };

  const generateAICaption = async () => {
    if (!aiContext.trim()) {
      toast.error('Please enter context for AI caption generation');
      return;
    }
    
    setGeneratingCaption(true);
    try {
      const response = await axios.post(`${API}/memes/generate-caption?context=${encodeURIComponent(aiContext)}`);
      setNewMemeCaption(response.data.caption);
      toast.success('AI caption generated!');
    } catch (error) {
      toast.error('Failed to generate caption');
      console.error('Error:', error);
    } finally {
      setGeneratingCaption(false);
    }
  };

  const createMeme = async () => {
    if (!newMemeCaption.trim()) {
      toast.error('Please enter a caption');
      return;
    }
    
    try {
      const payload = {
        caption: newMemeCaption,
        template: selectedTemplate,
        image_url: imageUrl || null
      };
      
      await axios.post(`${API}/memes`, null, {
        params: payload
      });
      
      toast.success('Meme created successfully!');
      setNewMemeCaption('');
      setImageUrl('');
      fetchMemes();
    } catch (error) {
      toast.error('Failed to create meme');
      console.error('Error:', error);
    }
  };

  const likeMeme = async (memeId) => {
    try {
      await axios.post(`${API}/memes/${memeId}/like`);
      fetchMemes();
    } catch (error) {
      toast.error('Failed to like meme');
    }
  };

  const shareMeme = async (meme, platform) => {
    const memeUrl = `${window.location.origin}/memes`;
    const text = `Check out this cricket meme: "${meme.caption}"`;
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(memeUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(memeUrl)}&quote=${encodeURIComponent(text)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + memeUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(memeUrl)}`
    };

    if (platform === 'native' && navigator.share) {
      // Use native Web Share API
      try {
        await navigator.share({
          title: 'Cricket Meme',
          text: meme.caption,
          url: memeUrl
        });
        toast.success('Shared!');
      } catch (error) {
        if (error.name !== 'AbortError') {
          toast.error('Sharing failed');
        }
      }
    } else if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const templates = [
    'drake-preference',
    'distracted-boyfriend',
    'two-buttons',
    'success-kid',
    'surprised-pikachu',
    'expanding-brain'
  ];

  return (
    <div className="min-h-screen" style={{ 
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)' 
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      {/* Header */}
      <div className={`backdrop-blur-md ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'} border-b ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button 
            onClick={() => navigate('/')} 
            className={`flex items-center ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors mb-4`}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </button>
          <h1 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} data-testid="page-title">Meme Studio 😂</h1>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-2`}>Create and share cricket memes powered by AI</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Create Meme */}
          <Card className={`glass-card lg:col-span-1 ${theme === 'light' ? 'bg-white/95' : ''}`} data-testid="create-meme-card">
            <h2 className={`text-2xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Create New Meme</h2>
            
            <div className="space-y-4">
              <div>
                <label className={`text-sm mb-2 block ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Select Template</label>
                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <SelectTrigger data-testid="template-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem key={template} value={template}>
                        {template.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* AI Caption Generator */}
              <div className={`p-4 rounded-lg border ${theme === 'dark' ? 'bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500/30' : 'bg-gradient-to-br from-purple-100 to-pink-100 border-purple-300'}`}>
                <div className="flex items-center space-x-2 mb-3">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  <span className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>AI Caption Generator</span>
                </div>
                <Input
                  placeholder="Enter context (e.g., 'USA beating Pakistan')..."
                  value={aiContext}
                  onChange={(e) => setAiContext(e.target.value)}
                  className={`mb-2 ${theme === 'dark' ? 'bg-white/10 border-white/20 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
                  data-testid="ai-context-input"
                />
                <Button 
                  onClick={generateAICaption}
                  disabled={generatingCaption}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500"
                  data-testid="generate-caption-button"
                >
                  {generatingCaption ? 'Generating...' : 'Generate AI Caption'}
                </Button>
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Caption</label>
                <Textarea
                  placeholder="Your meme caption..."
                  value={newMemeCaption}
                  onChange={(e) => setNewMemeCaption(e.target.value)}
                  rows={4}
                  className="bg-white/10 border-white/20 text-white"
                  data-testid="meme-caption-input"
                />
              </div>
              
              <div>
                <label className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-2 block`}>Image URL (Optional)</label>
                <Input
                  placeholder="https://images.unsplash.com/photo-..."
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}
                />
                <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>
                  Suggested: unsplash.com/s/photos/cricket (free cricket images)
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setImageUrl('https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=600')}
                    className={`text-xs px-2 py-1 rounded ${theme === 'dark' ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                  >
                    Cricket Ball
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageUrl('https://images.unsplash.com/photo-1531415074968-036ba1b575da?w=600')}
                    className={`text-xs px-2 py-1 rounded ${theme === 'dark' ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                  >
                    Stadium
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageUrl('https://images.unsplash.com/photo-1624526267942-ab0ff8a3e972?w=600')}
                    className={`text-xs px-2 py-1 rounded ${theme === 'dark' ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                  >
                    Action Shot
                  </button>
                </div>
              </div>
              
              <Button 
                onClick={createMeme}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600"
                data-testid="create-meme-button"
              >
                <Send className="w-4 h-4 mr-2" />
                Create Meme
              </Button>
            </div>
          </Card>

          {/* Meme Feed */}
          <div className="lg:col-span-2 space-y-6">
            <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} data-testid="meme-feed-title">Meme Feed</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {memes.map((meme) => (
                <Card 
                  key={meme.id} 
                  className={`glass-card hover:scale-105 transition-transform ${theme === 'light' ? 'bg-white/95' : ''}`}
                  data-testid={`meme-${meme.id}`}
                >
                  <div className="space-y-4">
                    {meme.image_url ? (
                      <div className="aspect-video rounded-lg overflow-hidden bg-gray-900">
                        <img 
                          src={meme.image_url} 
                          alt={meme.caption}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="aspect-video bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg flex items-center justify-center">
                        <span className="text-6xl">🏏</span>
                      </div>
                    )}
                    
                    <div>
                      <p className={`font-medium text-center mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`} data-testid={`meme-caption-${meme.id}`}>
                        {meme.caption}
                      </p>
                      <div className={`text-xs text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Template: {meme.template}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <button 
                        onClick={() => likeMeme(meme.id)}
                        className="flex items-center space-x-1 text-red-400 hover:text-red-300 transition-colors"
                        data-testid={`like-button-${meme.id}`}
                      >
                        <Heart className="w-5 h-5" />
                        <span data-testid={`meme-likes-${meme.id}`}>{meme.likes}</span>
                      </button>
                      
                      <div className="relative group">
                        <button className="flex items-center space-x-1 text-blue-400 hover:text-blue-300 transition-colors">
                          <Share2 className="w-5 h-5" />
                          <span>Share</span>
                        </button>
                        
                        {/* Share Dropdown */}
                        <div className={`absolute bottom-full right-0 mb-2 hidden group-hover:block ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl p-2 min-w-[120px] z-50`}>
                          <button onClick={() => shareMeme(meme, 'twitter')} className={`w-full text-left px-3 py-2 rounded hover:bg-blue-500/20 text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            𝕏 Twitter
                          </button>
                          <button onClick={() => shareMeme(meme, 'facebook')} className={`w-full text-left px-3 py-2 rounded hover:bg-blue-500/20 text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            📘 Facebook
                          </button>
                          <button onClick={() => shareMeme(meme, 'whatsapp')} className={`w-full text-left px-3 py-2 rounded hover:bg-green-500/20 text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            💬 WhatsApp
                          </button>
                          <button onClick={() => shareMeme(meme, 'linkedin')} className={`w-full text-left px-3 py-2 rounded hover:bg-blue-500/20 text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            🔗 LinkedIn
                          </button>
                          {navigator.share && (
                            <button onClick={() => shareMeme(meme, 'native')} className={`w-full text-left px-3 py-2 rounded hover:bg-purple-500/20 text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              📱 More...
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            
            {memes.length === 0 && (
              <div className="text-center py-16">
                <p className="text-gray-400 text-lg" data-testid="no-memes">No memes yet. Be the first to create one!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}